import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getAvatarColor } from '../utils/colors';
import type { TeacherListItem } from '../utils/types';

interface Props {
  teacher: TeacherListItem;
  onCapture: (photo: string, captureTime: Date) => void;
  onBack: () => void;
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const CameraScreen: React.FC<Props> = ({ teacher, onCapture, onBack }) => {
  const videoRef   = useRef<HTMLVideoElement>(null);
  const streamRef  = useRef<MediaStream | null>(null);
  const [ready, setReady]         = useState(false);
  const [permError, setPermError] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [flash, setFlash]         = useState(false);

  const startCamera = useCallback(async () => {
    setReady(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().then(() => { setReady(true); setPermError(''); });
        };
      }
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Camera access denied. Please allow camera in browser settings and try again.'
        : err?.name === 'NotFoundError'
        ? 'No camera found on this device.'
        : 'Could not access camera. Please try again.';
      setPermError(msg);
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [startCamera]);

  const handleCapture = () => {
    if (!videoRef.current || !ready || capturing) return;
    const captureTime = new Date();
    setCapturing(true);
    setFlash(true);
    setTimeout(() => setFlash(false), 200);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth  || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) { setCapturing(false); return; }

    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const photo = canvas.toDataURL('image/jpeg', 0.92);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    onCapture(photo, captureTime);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#000',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif", overflow: 'hidden',
    }}>
      {/* Camera view */}
      <video
        ref={videoRef}
        playsInline muted autoPlay
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover',
          transform: 'scaleX(-1)',
          transition: 'opacity 0.3s',
          opacity: ready ? 1 : 0,
        }}
      />

      {/* Flash overlay */}
      {flash && (
        <div style={{ position: 'absolute', inset: 0, background: 'white', opacity: 0.85, zIndex: 10, pointerEvents: 'none' }} />
      )}

      {/* Permission error */}
      {permError && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: 32, zIndex: 5,
        }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>📷</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: 10 }}>
            Camera Unavailable
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', textAlign: 'center', lineHeight: 1.5 }}>
            {permError}
          </div>
          <button onClick={() => startCamera()} style={{
            marginTop: 24, padding: '14px 28px', background: '#fff', border: 'none',
            borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#3b1f52',
          }}>
            Try Again
          </button>
        </div>
      )}

      {/* Loading */}
      {!ready && !permError && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
          <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {/* Top gradient overlay */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 120,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, transparent 100%)',
        zIndex: 6, pointerEvents: 'none',
      }} />

      {/* Top bar */}
      <div style={{
        position: 'relative', zIndex: 7, paddingTop: '20px 16px',
        padding: '20px 16px 20px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <button onClick={onBack} style={{
          width: 42, height: 42, borderRadius: '50%', border: 'none',
          background: 'rgba(0,0,0,0.38)', backdropFilter: 'blur(8px)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
        </button>

        {/* Teacher pill */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)',
          borderRadius: 20, padding: '6px 14px 6px 6px',
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: getAvatarColor(teacher.name),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, color: '#fff',
          }}>
            {initials(teacher.name)}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{teacher.name}</span>
        </div>

        <div style={{ width: 42, height: 42 }} />
      </div>

      {/* Bottom gradient */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 200,
        background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)',
        zIndex: 6, pointerEvents: 'none',
      }} />

      {/* Bottom controls */}
      <div style={{
        position: 'relative', zIndex: 7, marginTop: 'auto',
        padding: '0 40px env(safe-area-inset-bottom, 40px)',
        paddingBottom: 'max(env(safe-area-inset-bottom), 40px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
      }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
          Tap to capture photo
        </div>

        {/* Capture button */}
        <button
          onClick={handleCapture}
          disabled={!ready || capturing}
          style={{
            width: 76, height: 76, borderRadius: '50%', border: '4px solid #fff',
            background: capturing ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.92)',
            cursor: ready && !capturing ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
            transition: 'transform 0.1s, background 0.15s',
            transform: capturing ? 'scale(0.9)' : 'scale(1)',
          }}
        >
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: capturing ? '#fff' : '#3b1f52',
            transition: 'all 0.1s',
          }} />
        </button>
      </div>
    </div>
  );
};

export default CameraScreen;
